import { useMemo, useState } from 'react';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { Dimensions, Metrics } from 'components/insights/constants';
import { DrillableArrow, DrillableItem } from 'components/insights/layout/details/drillableTableComponents';
import { useTableConfig } from 'components/insights/layout/details/helpers/dimensionsUtils';
import useItemDrilldown from 'components/insights/layout/details/helpers/hooks/useItemDrilldown';
import MetricDetailBreadcrumbs from 'components/insights/layout/details/MetricDetailBreadcrumbs';
import TableHeader from 'components/insights/layout/details/TableHeader';
import TableTitle from 'components/insights/layout/details/TableTitle';
import AnalyticsResponseData from 'components/insights/query/AnalyticsResponseData';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { AnalyticsQueryParams } from 'components/insights/query/useAnalyticsInfiniteQuery';
import DataTable from 'components/shared/DataTable';
import { useProfile } from 'context/profile';
import { useTranslation } from 'context/translations';
import { useInsightsStore } from 'stores/insightsStore';

import ApiExport from '../ApiExport';
import { CategoryRow } from '../helpers/utils';
import MetricDiffBadge, { Comparator } from '../MetricDiffBadge';
import TableContainer from '../TableContainer';

export type AlosTableRow = CategoryRow & {
  alos: { current: number | null; prior: number | null };
};

type Props = {
  data: AnalyticsResponseData;
  loading: boolean;
  requestParams: AnalyticsQueryParams;
  rollups: AnalyticsResponseRollupCollection;
  onSelectedDimensionsChange: (selectedDimensions: Dimensions[]) => void;
  selectedDimensions: Dimensions[];
};

const AverageLengthOfStayDataTable = ({
  data,
  loading,
  requestParams,
  rollups,
  onSelectedDimensionsChange,
  selectedDimensions,
}: Props) => {
  const { profile } = useProfile();
  const { translate } = useTranslation();

  const selectedGroupType = useInsightsStore((state) => state.selectedGroupType);

  const { tableHeader, tableItemFilterKey, subDimensions } = useTableConfig(
    selectedDimensions[0],
    selectedGroupType,
    profile.canActAsManager,
    translate
  );

  const handleItemDrilldown = useItemDrilldown({
    series: data.dimensions.series,
    tableHeader,
    tableItemFilterKey,
    selectedDimensions,
    onSelectedDimensionsChange,
    subDimensions,
  });

  const [hoveredRowId, setHoveredRowId] = useState('');

  const categories = useMemo(() => data.dimensions.getCurrentDimensionValuesByIndex(0), [data.dimensions]);

  const lengthOfStayAverageRollup = rollups.overall.metrics.getCurrentDataPoint(Metrics.LENGTH_OF_STAY_AVERAGE, 0);
  const dischargeIdCountRollup = rollups.overall.metrics.getCurrentDataPoint(Metrics.ID_COUNT, 0);

  const dischargeIdCounts = data.metrics.getCurrentSeries(Metrics.ID_COUNT);
  const lengthOfStayAverages = data.metrics.getCurrentSeries(Metrics.LENGTH_OF_STAY_AVERAGE);
  const priorLengthOfStayAverages = data.metrics.getPriorSeries(Metrics.LENGTH_OF_STAY_AVERAGE);

  const tableData = useMemo<AlosTableRow[]>(() => {
    return categories.map((categoryName, i) => ({
      categoryName,
      alos: {
        current: lengthOfStayAverages[i],
        prior: priorLengthOfStayAverages[i],
      },
      locationEpisodes: dischargeIdCounts[i],
    }));
  }, [categories, lengthOfStayAverages, dischargeIdCounts, priorLengthOfStayAverages]);

  const columnHelper = createColumnHelper<AlosTableRow>();
  const columns = useMemo<ColumnDef<AlosTableRow, any>[]>(
    () => [
      columnHelper.accessor('categoryName', {
        header: tableHeader,
        cell: (cellData) => (
          <DrillableItem drillable={!!subDimensions} isRowHovered={hoveredRowId === cellData.row.id}>
            {cellData.getValue()}
          </DrillableItem>
        ),
      }),
      columnHelper.accessor('locationEpisodes', {
        header: 'Patients',
        cell: (data) => data.getValue() ?? <>&mdash;</>,
        footer: () => `${dischargeIdCountRollup ?? '—'} (Total)`,
      }),
      columnHelper.accessor('alos', {
        header: 'Average Length of Stay',
        cell: (data) => (
          <>
            <p>{data.getValue().current == null ? <>&mdash;</> : `${data.getValue().current.toFixed(1)} days`}</p>{' '}
            <MetricDiffBadge
              currentValue={data.getValue().current}
              priorValue={data.getValue().prior}
              comparator={Comparator.LESS_THAN}
              formatter={(val) => `${val.toFixed(1)}`}
            />
            {subDimensions && hoveredRowId === data.row.id && <DrillableArrow width={9} height={9} />}
          </>
        ),
        footer: () => `${lengthOfStayAverageRollup?.toFixed(1) ?? '—'} days (Avg)`,
        sortingFn: (a, b) => {
          return (a.original.alos.current ?? 0) - (b.original.alos.current ?? 0);
        },
      }),
    ],
    [columnHelper, tableHeader, subDimensions, hoveredRowId, dischargeIdCountRollup, lengthOfStayAverageRollup]
  );

  const desktopOnlyColumns = ['locationEpisodes'];

  const defaultSort = [
    {
      id: 'locationEpisodes',
      desc: true,
    },
  ];

  return (
    <TableContainer>
      <TableHeader>
        <TableHeader.Row>
          <TableTitle>Average Length of Stay Overview</TableTitle>
          <ApiExport
            disabled={!tableData.length}
            show={!loading}
            params={requestParams}
            filename='average-length-of-stay-export'
          />
        </TableHeader.Row>
        <MetricDetailBreadcrumbs onSelectedDimensionsChange={onSelectedDimensionsChange} />
      </TableHeader>
      <DataTable
        columns={columns}
        data={tableData}
        defaultSortBy={defaultSort}
        desktopOnlyColumns={desktopOnlyColumns}
        loading={loading}
        drillable={!!subDimensions}
        setHoveredRowId={setHoveredRowId}
        onRowClick={handleItemDrilldown}
      />
    </TableContainer>
  );
};

export default AverageLengthOfStayDataTable;
